import React, { useCallback, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { ImportImageContainer } from '../style';
import { colors } from 'theme/colors';

const MAX_SIZE_MB = 5;
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

interface IFileDropzone {
  onFileSelect?: (files: File[]) => void;
  disabled?: boolean;
  externalErrors?: string[];
}

export const FileDropzoneContainer = ({
  onFileSelect,
  disabled,
  externalErrors = [],
}: IFileDropzone) => {
  const { t } = useTranslations();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const validateFiles = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return [];

    const validFiles: File[] = [];

    Array.from(selectedFiles).forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(t('GalleryImageFormatException'));
        return;
      }

      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(t('GalleryImageSizeException'));
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setError(null);
    }

    return validFiles;
  };

  const handleFiles = useCallback(
    (selectedFiles: FileList | null) => {
      const validated = validateFiles(selectedFiles);
      if (validated.length > 0) {
        setFiles((prev) => [...prev, ...validated]);
        onFileSelect?.(validated);
      }
    },
    [onFileSelect],
  );

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      handleFiles(e.target.files);
    }
  };

  const hasError = !!error || externalErrors.length > 0;

  return (
    <Grid
      container
      sx={{ flex: '1 1 auto', minHeight: 0, flexDirection: 'column', flexWrap: 'nowrap' }}
    >
      <ImportImageContainer
        container
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => document.getElementById('fileInput')?.click()}
        style={{ cursor: 'pointer' }}
        disabled={disabled}
        hasError={hasError}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Typography variant="body2" sx={{ color: colors.primary600 }}>
            {t('manageImages.addPhoto.label')}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.gray400 }}>
            {t('manageImages.dragDrop.label')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <Typography variant="caption" sx={{ color: colors.gray400 }}>
            {t('manageImages.allowedFileFormats.label')}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray400 }}>
            {t('manageImages.maxSizeMB.label').replace('{0}', MAX_SIZE_MB.toString())}
          </Typography>
        </Box>

        <input
          id="fileInput"
          type="file"
          accept="image/png, image/jpeg"
          hidden
          onChange={handleFileInputChange}
          multiple
        />
      </ImportImageContainer>
      {error && (
        <Grid sx={{ pt: '5px', justifyContent: 'start' }}>
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        </Grid>
      )}
      {externalErrors.map((externalError) => (
        <Grid key={externalError} sx={{ pt: '5px', justifyContent: 'start' }}>
          <Typography variant="caption" color="error">
            {externalError}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};
